// @ts-strict-ignore
import { useEffect, useRef, useState } from 'react';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { routes } from '~/helpers/routes';

// Per this article: https://stackoverflow.com/questions/3468607
// setTimeout's millisecond delay maxes out at the highest number possible in 32-bit computing
const SETTIMEOUT_MAXIMUM = 2147483647;

type UseIdleSessionProps = {
  /**
   * How frequently to throttle the trackActivity callback
   * @default 1_000 (1 second)
   */
  activityThrottleTimeMS?: number;

  /**
   * How many miliseconds before expiration a user should be marked as idle
   * @default 30_000 (30 seconds)
  */
  idleWarnTimeMS?: number;

  /**
  * How long a user can be idle before the session expires
  * @default 600_000 (10 minutes)
  */
  sessionExpireTimeMS?: number;

  /**
   * A callback to run when the user session expires
   */
  onSessionExpire: () => void;
};

export const useIdleSession = ({
  activityThrottleTimeMS = 1_000,
  idleWarnTimeMS = 30_000,
  sessionExpireTimeMS = 600_000,
  onSessionExpire,
}: UseIdleSessionProps) => {
  const [isIdle, setIsIdle] = useState(false);
  const latestActivity = useRef(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const trackActivity = () => {
    const now = Date.now();
    // Throttle updating the localStorage to once per activityThrottleTimeMS
    if ((now - latestActivity.current) < activityThrottleTimeMS) return;

    latestActivity.current = now;
  };

  /**
  * Ping the Daffy servers with an updated user activity time, to prevent/trigger other windows session timeout functionality
  *
  * @param idleForMS The number of miliseconds the user has been idle for
  * @returns Either the number of seconds the user has been inactive for, or the string 'logout' if the user should be logged out
  */
  const pingActivity = async (idleForMS: number = 0) => {
    try {
      // Daffy endpoint expects idleFor param in integer seconds
      const idleForSeconds = Math.floor(idleForMS / 1_000);
      const params = buildURLParamsString({ idleFor: idleForSeconds });
      const data = await ajaxFetch(`${routes.sessionPing}${params}`) as ('logout' | number);

      // If the value received back from Daffy server has a newer activity date than locally available, update latestActivity locally
      if (typeof data === 'number') {
        const serverLatestActivity = new Date().setSeconds(
          new Date(latestActivity.current).getSeconds() - data,
        );

        latestActivity.current = Math.max(latestActivity.current, serverLatestActivity);
      }

      return data;
    } catch (err) {
      return 0;
    }
  };

  const checkActivity = async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const now = Date.now();
    const idleForMS = Math.floor(now - latestActivity.current);
    const data = await pingActivity(idleForMS);

    const shouldExpire = idleForMS >= sessionExpireTimeMS || data === 'logout';
    const shouldMarkIdle = idleForMS >= sessionExpireTimeMS - idleWarnTimeMS;

    let nextCheckActivityInterval = 600_000; // 1 min

    // If idle for longer than session expire time, then expire user session and don't set another timeout
    if (shouldExpire) {
      return onSessionExpire();
    }

    if (shouldMarkIdle) {
      setIsIdle(true);
      nextCheckActivityInterval = Math.min(
        nextCheckActivityInterval,
        sessionExpireTimeMS - idleForMS,
      );
    } else {
      setIsIdle(false);
      nextCheckActivityInterval = Math.min(
        nextCheckActivityInterval,
        sessionExpireTimeMS - idleWarnTimeMS - idleForMS,
      );
    }

    timeoutRef.current = setTimeout(
      checkActivity,
      // Sanity check to make sure we aren't breaking JS timeout logic
      Math.min(nextCheckActivityInterval, SETTIMEOUT_MAXIMUM),
    );

    return undefined;
  };

  // RUN ON COMPONENT MOUNT
  useEffect(() => {
    const trackedEvents = ['mousemove', 'scroll', 'keyup', 'click'] as const;

    for (let i = 0; i < trackedEvents.length; i += 1) {
      document.addEventListener(trackedEvents[i], trackActivity);
    }

    checkActivity();

    // RUN ON COMPONENT WILL UNMOUNT
    return () => {
      clearTimeout(timeoutRef.current);
      for (let i = 0; i < trackedEvents.length; i += 1) {
        document.removeEventListener(trackedEvents[i], trackActivity);
      }
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on MOUNT, UNMOUNT, & when sessionExpireTimeMS updates
  [sessionExpireTimeMS]);

  return {
    isIdle, setIsIdle,
  };
};
